import { action, makeObservable, observable } from 'mobx';

import { isBoolean } from 'lodash';

/**
 * StudentAssignmentPlayerLauncherManager
 *
 * Holds the MobX state for a given student assignment/resource currently
 * being launched in one of the various player modals (LessonPlayer,
 * FileViewer, DocReader, Learnosity, etc.).
 *
 * Can be set by StudentAssignmentPlayerLauncherService.launchAssignment;
 * can be cleared by StudentAssignmentPlayerLauncherService.closeActivePlayer
 * (or clearActivePlayer() directly).
 *
 * This manager only owns active launcher state — which player is showing
 * right now, and the data its modal needs. Long-lived student assignment
 * state (satellite flags, menu status order, display config, etc.) lives
 * on StudentAssignmentManager.
 */
export class StudentAssignmentPlayerLauncherManager {
  @observable activePlayer = null;

  @observable isLaunchingAssignment = false;

  @observable activityId = null;

  @observable assignerId = null;

  @observable assignmentId = null;

  @observable contentImageUrl = null;

  @observable contentItemDescription = null;

  @observable contentItemId = null;

  @observable contentMode = null;

  @observable contentName = null;

  @observable contentSubtitle = null;

  @observable contentUrl = null;

  @observable grade = null;

  @observable instruction = null;

  @observable isFlowpaper = false;

  @observable previewContentType = null;

  @observable resourceName = null;

  constructor() {
    makeObservable(this);
  }

  @action setActivePlayer = (activePlayer, playerProps = {}) => {
    this.activePlayer = activePlayer;
    this.activityId = playerProps.activityId || null;
    this.assignerId = playerProps.assignerId || null;
    this.assignmentId = playerProps.assignmentId || null;
    this.contentImageUrl = playerProps.contentImageUrl || null;
    this.contentItemDescription = playerProps.contentItemDescription || null;
    this.contentItemId = playerProps.contentItemId || null;
    this.contentMode = playerProps.contentMode || null;
    this.contentName = playerProps.contentName || null;
    this.contentSubtitle = playerProps.contentSubtitle || null;
    this.contentUrl = playerProps.contentUrl || null;
    this.grade = (!!playerProps.grade || playerProps.grade === 0) ? playerProps.grade : null;
    this.instruction = playerProps.instruction || null;
    this.isFlowpaper = isBoolean(playerProps.isFlowpaper) ? playerProps.isFlowpaper : false;
    this.previewContentType = playerProps.previewContentType || null;
    this.resourceName = playerProps.resourceName || null;
  }

  @action setIsLaunchingAssignment = (toggle) => {
    this.isLaunchingAssignment = toggle;
  }

  @action clearAll = () => {
    this.clearActivePlayer();
    this.isLaunchingAssignment = false;
  }

  @action clearActivePlayer = () => {
    this.activePlayer = null;
    this.activityId = null;
    this.assignerId = null;
    this.assignmentId = null;
    this.contentImageUrl = null;
    this.contentItemDescription = null;
    this.contentItemId = null;
    this.contentMode = null;
    this.contentName = null;
    this.contentSubtitle = null;
    this.contentUrl = null;
    this.grade = null;
    this.instruction = null;
    this.isFlowpaper = false;
    this.previewContentType = null;
    this.resourceName = null;
  }
}

export default new StudentAssignmentPlayerLauncherManager();
